import { DownloadIcon } from "@chakra-ui/icons";
import { Box, Center, Flex, Grid, Image, Text } from "@chakra-ui/react";
import { sortArrayBy } from "utils/filter-array-by";
import { GenericFileIcon, JSONFileIcon } from "components/vectors/bin-icon";
import { CircleProgress } from "components/vectors/circle-progress";
import { changeProgress } from "components/vectors/circle-progress/change-progress-interface";
import { CSVIcon } from "components/vectors/csv-icon";
import { DownloadCircleIcon } from "components/vectors/download-icon";
import { ExelIcon } from "components/vectors/file-extensions-icons";
import { ImageIcon } from "components/vectors/image-icon";
import { PDFIcon } from "components/vectors/pdf-icon";
import { memo, useEffect, useRef, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { createWebSocketEventListener } from "utils/web-socket-events";
import { AudioMessagePlayer } from "./audio/audio-message-player";
import { addLinks } from "./functions";
import { MessageContainer } from "./message-container";
import {
  WhatsAppMessagesHelper,
  whatsappMessageStore,
} from "components/atendimentos-components/whatsapp-attendances/components/whats-app-chat/hooks/use-whats-app-session-controls/ws-messages-api";
import { UseWebSocketProps } from "components/atendimentos-components/use-init-web-socket/types";
import { openModalWhatsAppImage } from "./modal-image";
import { CachedMessage } from "utils/whatsapp-cached-messages";
import { createLinkAndDownload } from "utils/create-link-and-download";
import { TextFileIcon } from "components/vectors/text-file-icon";
import { Message } from "components/atendimentos-components/whatsapp-attendances/components/whats-app-chat/hooks/use-whats-app-session-controls/types";

interface MediaPart {
  requestedMedia: {
    chatContactId: string;
    dataPart: string;
    filesize: number;
    messageId: string;
    mimetype: string;
    part: number;
    amountParts: number;
    type: string;
  };
}

function MessageTemplate(message: Message & { wsSession: UseWebSocketProps }) {
  const {
    type,
    body,
    direction,
    id,
    filename,
    mimetype,
    userFullName,
    wsSession,
  } = message;
  const isMyMessage = direction === "o";
  const [isLoading, setLoading] = useState(false);
  const isRevoked = type === "revoked";
  let [mediaURL, setMediaUrl] = useState("");
  const mediaParts = useRef<Map<number, string>>(new Map());
  const mediaMimetype = useRef<string | undefined>(undefined);
  const [isDownloadedMedia, setIsDownloadedMedia] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const whatsAppWsApi = new WhatsAppMessagesHelper(wsSession);

  const setCircleProgress = (value: number, isVisible?: boolean) => {
    changeProgress(`circle-progress-${id}`, { value, isVisible });
    if (isVisible === false) {
    }
  };

  useEffect(() => {
    const fetchCachedMedia = async () => {
      const messageData = await whatsappMessageStore.getMessage(message.id);
      if (messageData) {
        const url = URL.createObjectURL(messageData.data);
        setMediaUrl(url);
        mediaURL = url;
        setIsDownloadedMedia(true);
      }
    };
    if (
      type === "image" ||
      type === "video" ||
      type === "document" ||
      type === "ptt" ||
      type === "audio" ||
      type === "sticker"
    ) {
      fetchCachedMedia();
    }
  }, [message.id, type]);

  createWebSocketEventListener(
    "requested-media",
    async (data) => {
      const { requestedMedia }: MediaPart = data;
      const target = mediaParts.current;
      setLoading(true);
      target.set(requestedMedia.part, requestedMedia.dataPart);
      const progress = (target.size / requestedMedia.amountParts) * 100;
      setCircleProgress(progress);
      if (progress === 100) {
        setCircleProgress(progress, false);

        const mediaPartsArray = Array.from(target.entries()).map(
          ([key, value]) => ({ key, value })
        );
        const base64Content = sortArrayBy(mediaPartsArray, "key", "ASC")
          .map(({ value }) => value)
          .join("");
        const withHeader = `data:${requestedMedia.mimetype};base64,${base64Content}`;
        setLoading(false);

        const resp = await fetch(withHeader);
        const blob = await resp.blob();
        // Save in indexedDB
        await whatsappMessageStore.saveMessage({
          data: blob,
          id: message.id,
          type: message.type as CachedMessage["type"],
        });

        const file = new File(
          [blob],
          `whatsapp-file-${new Date().getTime()}.${
            requestedMedia.mimetype.split("/")[1].split(";")[0]
          }`,
          { type: requestedMedia.mimetype }
        );

        mediaMimetype.current = requestedMedia.mimetype;

        const url = URL.createObjectURL(file);

        if (type === "image") {
          setMediaUrl(url);
          mediaURL = url;
          setIsDownloadedMedia(true);
        } else if (type === "video") {
          setMediaUrl(url);
          mediaURL = url;
          setIsDownloadedMedia(true);
        } else if (type === "document") {
          setMediaUrl(url);
          mediaURL = url;
          setIsDownloadedMedia(true);
        } else if (type === "ptt" || type === "audio") {
          setMediaUrl(url);
          mediaURL = url;
          setIsDownloadedMedia(true);
        } else if (type === "sticker") {
          setMediaUrl(url);
          mediaURL = url;
          setIsDownloadedMedia(true);
        }

        mediaParts.current = new Map();
      }
    },
    id
  );

  if (type === "chat" || isRevoked) {
    return (
      <MessageContainer isMyMessage={isMyMessage} message={message}>
        {userFullName ? (
          <Text fontSize="14" fontWeight="bold">
            {userFullName}
          </Text>
        ) : null}
        <Text
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          lineHeight="19px"
          fontSize="14"
          fontStyle={isRevoked ? "italic" : "normal"}
          fontWeight="500"
          opacity={isRevoked ? "0.6" : undefined}
        >
          {isRevoked ? "Mensagem Apagada" : addLinks(body || "")}
        </Text>
      </MessageContainer>
    );
  } else if (type === "image") {
    return (
      <MessageContainer isMyMessage={isMyMessage} message={message}>
        {userFullName ? (
          <Text px="2" fontSize="14" fontWeight="bold">
            {userFullName}
          </Text>
        ) : null}
        <Flex
          w="100%"
          borderRadius="10px"
          overflow="hidden"
          alignItems="start"
          minH={body || mediaURL ? undefined : "180px"}
          maxH="240px"
          onClick={() => {
            if (isDownloadedMedia) {
              openModalWhatsAppImage({
                src: mediaURL,
                type: "image",
                mimetype: mediaMimetype.current,
              });
            } else {
              whatsAppWsApi.requestMedia(message);
            }
          }}
          pos="relative"
          cursor="pointer"
        >
          {body || mediaURL ? (
            <Image
              src={mediaURL ? mediaURL : `data:image/jpeg;base64,${body}`}
              w="100%"
            />
          ) : (
            <ImageIcon width="40%" display="block" m="auto" />
          )}
          <Flex
            alignItems="center"
            justifyContent="center"
            pos="absolute"
            inset={0}
          >
            {isLoading ? null : isDownloadedMedia ? null : (
              <Flex
                justifyContent="center"
                alignItems="center"
                w="50px"
                h="50px"
                bg="rgba(0,0,0,0.4)"
                borderRadius="50%"
                color="white"
              >
                <DownloadIcon />
              </Flex>
            )}

            <Box
              bg="rgba(0,0,0,0.4)"
              borderRadius="50%"
              width="50px"
              height="50px"
              display={isLoading ? "block" : "none"}
            >
              <CircleProgress id={id} isVisible={isLoading} />
            </Box>
          </Flex>
        </Flex>
      </MessageContainer>
    );
  } else if (type === "ptt" || type === "audio") {
    return (
      <MessageContainer isMyMessage={isMyMessage} message={message}>
        <AudioMessagePlayer
          src={mediaURL}
          id={id}
          isDownloaded={isDownloadedMedia}
          myMessage={isMyMessage}
          isLoading={isLoading}
          message={message}
          wsSession={wsSession}
        />
      </MessageContainer>
    );
  } else if (type === "video") {
    return (
      <MessageContainer isMyMessage={isMyMessage} message={message}>
        {userFullName ? (
          <Text px="2" fontSize="14" fontWeight="bold">
            {userFullName}
          </Text>
        ) : null}
        <Flex
          w="100%"
          borderRadius="10px"
          overflow="hidden"
          minH="180px"
          maxH="240px"
          onClick={() => {
            if (isDownloadedMedia) {
              openModalWhatsAppImage({
                src: mediaURL,
                type: "video",
                mimetype: mediaMimetype.current,
              });
            } else {
              whatsAppWsApi.requestMedia(message);
            }
          }}
          pos="relative"
          cursor="pointer"
        >
          {body ? (
            <Image
              src={`data:image/jpeg;base64,${body}`}
              w="100%"
              h="auto"
              objectFit="cover"
            />
          ) : null}
          <Flex
            alignItems="center"
            justifyContent="center"
            pos="absolute"
            inset={0}
          >
            {isLoading ? null : isDownloadedMedia ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                w="50px"
                h="50px"
                bg="rgba(0,0,0,0.4)"
                borderRadius="50%"
                color="white"
              >
                <FaPlay />
              </Flex>
            ) : (
              <Flex
                justifyContent="center"
                alignItems="center"
                w="50px"
                h="50px"
                bg="rgba(0,0,0,0.4)"
                borderRadius="50%"
                color="white"
              >
                <DownloadIcon />
              </Flex>
            )}
            <Box
              bg="rgba(0,0,0,0.4)"
              borderRadius="50%"
              width="50px"
              height="50px"
              display={isLoading ? "block" : "none"}
            >
              <CircleProgress id={id} isVisible={isLoading} />
            </Box>
          </Flex>
        </Flex>
      </MessageContainer>
    );
  } else if (type === "document") {
    return (
      <MessageContainer isMyMessage={isMyMessage} message={message}>
        {userFullName ? (
          <Text px="2" fontSize="14" fontWeight="bold">
            {userFullName}
          </Text>
        ) : null}
        <Box
          onClick={() => {
            if (isDownloadedMedia) {
              if (filename?.endsWith(".pdf")) {
                window.open(mediaURL, "_blank");
              } else {
                createLinkAndDownload(mediaURL, filename || "document");
              }
            } else {
              whatsAppWsApi.requestMedia(message);
            }
          }}
          cursor={"pointer"}
          bg="#fff"
          borderRadius="5px"
        >
          {body ? (
            <Box
              w="100%"
              bgImg={`data:image/jpeg;base64,${body}`}
              sx={{ aspectRatio: `${21 / 9}` }}
              bgSize="contain"
              bgRepeat="no-repeat"
            />
          ) : (
            <Center
              w="100%"
              sx={{ aspectRatio: `${21 / 9}` }}
              overflow="hidden"
            >
              {filename?.endsWith(".pdf") ? (
                <PDFIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                />
              ) : filename?.endsWith(".csv") ? (
                <CSVIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                />
              ) : filename?.endsWith(".xlsx") ? (
                <ExelIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                />
              ) : filename?.endsWith(".txt") ? (
                <TextFileIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                />
              ) : filename?.endsWith(".json") ? (
                <JSONFileIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                />
              ) : (
                <GenericFileIcon
                  width="75px"
                  height="auto"
                  transform="translateY(25px)"
                  colors={[
                    "var(--chakra-colors-gray-200)",
                    "var(--chakra-colors-gray-100)",
                    "white",
                  ]}
                />
              )}
            </Center>
          )}

          <Grid
            alignItems="center"
            templateColumns="30px auto 30px"
            px="8px"
            borderRadius="6px"
            py="14px"
            bg="#f5f7f6"
            gap="8px"
          >
            <Flex alignItems="center" justifyContent="center">
              {filename?.endsWith(".pdf") ? (
                <PDFIcon />
              ) : filename?.endsWith(".csv") ? (
                <CSVIcon />
              ) : filename?.endsWith(".xlsx") ? (
                <ExelIcon />
              ) : filename?.endsWith(".txt") ? (
                <TextFileIcon />
              ) : filename?.endsWith(".json") ? (
                <JSONFileIcon />
              ) : (
                <GenericFileIcon />
              )}
            </Flex>
            <Text
              fontSize="13"
              lineHeight="1"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              color={filename ? "black" : "gray.500"}
            >
              {filename || "Documento sem nome"}
            </Text>
            <Flex alignItems="center" justifyContent="center">
              {!isDownloadedMedia && (
                <Box
                  onClick={() => {
                    if (isDownloadedMedia) {
                      linkRef.current!.href = mediaURL;
                      if (!(mimetype === "application/pdf"))
                        linkRef.current!.download =
                          filename || "Documento sem nome";
                      linkRef.current?.click();
                    } else {
                      whatsAppWsApi.requestMedia(message);
                    }
                  }}
                  borderRadius="50%"
                  _hover={{ bg: "rgba(0,0,0, 0.05)" }}
                  transition="0.3s"
                  cursor={!isLoading ? "pointer" : undefined}
                >
                  <CircleProgress
                    id={id}
                    isVisible={isLoading}
                    width="24px"
                    height="24px"
                  />
                  {isLoading ? null : (
                    <DownloadCircleIcon color="var(--chakra-colors-gray-400)" />
                  )}
                </Box>
              )}
            </Flex>
          </Grid>
        </Box>
      </MessageContainer>
    );
  } else if (type === "sticker") {
    return (
      <MessageContainer
        isMyMessage={isMyMessage}
        containerProps={{ w: "auto", bg: "transparent" }}
        messageArrowProps={{ opacity: 0 }}
        message={message}
      >
        <Flex
          w="120px"
          h={body || mediaURL ? "auto" : "120px"}
          borderRadius="10px"
          overflow="hidden"
          alignItems="center"
          justifyContent="center"
          onClick={() => {
            if (isDownloadedMedia) {
              openModalWhatsAppImage({
                src: mediaURL,
                type: "sticker",
                mimetype: mediaMimetype.current,
              });
            } else {
              whatsAppWsApi.requestMedia(message);
            }
          }}
          pos="relative"
          cursor="pointer"
        >
          {body || mediaURL ? (
            <Image
              src={mediaURL ? mediaURL : `data:image/jpeg;base64,${body}`}
              w="100%"
            />
          ) : (
            <ImageIcon width="40%" display="block" />
          )}
          <Flex
            alignItems="center"
            justifyContent="center"
            pos="absolute"
            inset={0}
          >
            {isLoading ? null : isDownloadedMedia ? null : (
              <Flex
                justifyContent="center"
                alignItems="center"
                w="30px"
                h="30px"
                bg="rgba(0,0,0,0.4)"
                borderRadius="50%"
                color="white"
              >
                <DownloadIcon />
              </Flex>
            )}

            <Box
              bg="rgba(0,0,0,0.4)"
              borderRadius="50%"
              width="30px"
              height="30px"
              display={isLoading ? "block" : "none"}
            >
              <CircleProgress id={id} isVisible={isLoading} />
            </Box>
          </Flex>
        </Flex>
      </MessageContainer>
    );
  }
  return null;
}
export default memo(MessageTemplate, (prevProps, currProps) => {
  const compareProps: any = {
    attendanceId: prevProps.attendanceId === currProps.attendanceId,
    body: prevProps.body === currProps.body,
    direction: prevProps.direction === currProps.direction,
    from: prevProps.from === currProps.from,
    id: prevProps.id === currProps.id,
    to: prevProps.to === currProps.to,
    type: prevProps.type === currProps.type,
    duration: prevProps.duration === currProps.duration,
    filename: prevProps.filename === currProps.filename,
    mediaKey: prevProps.mediaKey === currProps.mediaKey,
    mimetype: prevProps.mimetype === currProps.mimetype,
    url: prevProps.url === currProps.url,
    userFullName: prevProps.userFullName === currProps.userFullName,
  };

  const isEquals = !Object.keys(compareProps).find((k) => !compareProps[k]);
  return isEquals;
});
